import { rememberSignOut, flushSignOuts } from './sessionRevocation';
import { WorkspaceCoach } from './WorkspaceCoach';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, Navigate, useLocation } from 'react-router-dom';
import { NavBarLogo } from './NavBarLogo';
import { WorkspaceConversations, workspaceRequest } from './WorkspaceViews';
import { WorkspacePeople, WorkspaceResults } from './WorkspaceScreens';
import {
  AppLink,
  ImpersonationBanner,
  authHeaders,
  impersonatedUserId,
  useAdminSession,
} from './impersonation';
import { MessagingConnectionCard } from '../../shared/src/components/MessagingConnectionCard';
import {
  providerDevices,
  providerDeviceStatus,
  deviceSyncNotice,
  type ConnectionDevice as DeviceRecord,
} from './connectionStatus';
import { AUTO_SYNC_PROVIDERS } from '../../shared/src/types/connections';
import { formatRelativeTime } from '../../shared/src/utils/relativeTime';
import type { WorkspaceSummary } from '../../shared/src/types/workspace';
import type { OwnProfile } from '../../shared/src/types/people';
import { useOwnPhoto } from '../../shared/src/hooks/useOwnPhoto';
import { loadOwnProfile } from '../../shared/src/utils/httpClients';
import { webRequest } from './api';
import { isTokenExpired, sessionClaims } from './session';
import {
  MessengerButtons,
  MessengerConnect,
  messengerNames,
  useCloudConnections,
  useMessengersAvailable,
} from './MessengerConnect';
import type { CloudProvider } from '../../shared/src/types/connect';

interface OutboundRequest {
  requestId: string;
  platform: string;
  recipient: string;
  text: string;
  createdAt: number;
  updatedAt: number;
  status: 'queued' | 'sending' | 'sent' | 'failed';
  errorCode?: string;
  error?: string;
  sentMessageId?: string;
}

interface RealtimeSnapshot {
  devices: DeviceRecord[];
  outbox: OutboundRequest[];
}

interface RealtimeEnvelope {
  type: string;
  data?: unknown;
}

const RealtimePage: React.FC = () => {
  useEffect(() => {
    void flushSignOuts();
    const retry = () => void flushSignOuts();
    window.addEventListener('online', retry);
    return () => window.removeEventListener('online', retry);
  }, []);
  const location = useLocation();
  const [, , tab = 'coach', opened] = location.pathname.split('/');
  // The result or person open in Results or Relationships.
  const detail = useMemo(() => {
    try {
      return opened ? decodeURIComponent(opened) : null;
    } catch {
      return null;
    }
  }, [opened]);
  const isSettings = tab === 'settings' || tab === 'devices';
  const isResults = tab === 'results' || tab === 'insights';
  const isPeople = tab === 'people';
  const isAsk = !isSettings && !isResults && !isPeople && tab !== 'conversations';
  const [workspace, setWorkspace] = useState<WorkspaceSummary | null>(null);
  const [workspaceError, setWorkspaceError] = useState('');
  const [refresh, setRefresh] = useState(0);
  const [loadingMore, setLoadingMore] = useState(false);
  const conversationLimit = useRef(100);
  const [token, setToken] = useState(() => localStorage.getItem('soulthread.session') || '');
  const ownPhoto = useOwnPhoto(
    () =>
      token ? loadOwnProfile(webRequest(token)) : Promise.resolve<OwnProfile>({ photoUrl: null }),
    token
  );
  const [error, setError] = useState('');
  const [connectionState, setConnectionState] = useState<'offline' | 'connecting' | 'live'>(
    'offline'
  );
  const [snapshot, setSnapshot] = useState<RealtimeSnapshot>({ devices: [], outbox: [] });
  const [platform, setPlatform] = useState('imessage');
  const [recipient, setRecipient] = useState('');
  const [messageText, setMessageText] = useState('');
  const [sending, setSending] = useState(false);
  const socketRef = useRef<WebSocket | null>(null);
  const sessionTokenRef = useRef(token);
  sessionTokenRef.current = token;

  const userId = useMemo(() => (token ? (sessionClaims(token)?.userId ?? null) : null), [token]);
  const adminSession = useAdminSession(token);
  const isStaff = adminSession?.isAdmin === true;
  const viewingAs = adminSession?.viewingAs ?? null;
  // Known from the address bar at once; the live connection stays closed while viewing.
  const viewingSomeone = Boolean(impersonatedUserId());
  const cloud = useCloudConnections(viewingSomeone ? null : token);
  const messengers = useMessengersAvailable() ?? [];
  const [connecting, setConnecting] = useState<CloudProvider | null>(null);
  /** Messengers kept in sync on SoulThread's servers now. */
  const activeOnServers = cloud.connections
    .filter((connection) => connection.status === 'active')
    .map((connection) => connection.provider);
  const connectable = messengers.filter((provider) => !activeOnServers.includes(provider));

  const clearSession = useCallback(() => {
    if (token) rememberSignOut(token);
    sessionTokenRef.current = '';
    if (localStorage.getItem('soulthread.session') === token)
      localStorage.removeItem('soulthread.session');
    setToken('');
    setWorkspace(null);
    conversationLimit.current = 100;
    setError('');
    setMessageText('');
    setRecipient('');
    setSnapshot({ devices: [], outbox: [] });
    setConnectionState('offline');
  }, [token]);

  useEffect(() => {
    const onStorage = (event: StorageEvent) => {
      if (
        (event.key === 'soulthread.session' || event.key === null) &&
        localStorage.getItem('soulthread.session') !== sessionTokenRef.current
      ) {
        const next = localStorage.getItem('soulthread.session') || '';
        clearSession();
        if (next && !isTokenExpired(next)) setToken(next);
      }
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, [clearSession]);

  useEffect(() => {
    if (token && (isTokenExpired(token) || !userId)) clearSession();
  }, [clearSession, token, userId]);

  useEffect(() => {
    window.addEventListener('soulthread:session-expired', clearSession);
    return () => window.removeEventListener('soulthread:session-expired', clearSession);
  }, [clearSession]);
  useEffect(() => {
    const recipient = new URLSearchParams(location.search).get('recipient');
    if (recipient && isSettings) setRecipient(recipient);
  }, [location.search, isSettings]);
  useEffect(() => {
    if (!token || !userId) return;
    const controller = new AbortController();
    setWorkspaceError('');
    workspaceRequest<WorkspaceSummary>(
      token,
      '/?limit=' + conversationLimit.current,
      controller.signal
    )
      .then((value) => {
        if (!controller.signal.aborted) setWorkspace(value);
      })
      .catch((failure) => {
        if (!controller.signal.aborted) setWorkspaceError(failure.message);
      });
    return () => controller.abort();
  }, [token, userId, refresh]);
  const loadMoreConversations = async () => {
    if (loadingMore || workspace?.nextOffset == null) return;
    setLoadingMore(true);
    conversationLimit.current = Math.min(5000, conversationLimit.current + 100);
    try {
      const value = await workspaceRequest<WorkspaceSummary>(
        token,
        '/?limit=' + conversationLimit.current
      );
      if (sessionTokenRef.current !== token) return;
      setWorkspace(value);
    } catch (failure) {
      setWorkspaceError(
        failure instanceof Error ? failure.message : 'Unable to load conversations.'
      );
    } finally {
      setLoadingMore(false);
    }
  };

  const applySnapshot = useCallback((value: unknown) => {
    if (!value || typeof value !== 'object') return;
    const data = value as Partial<RealtimeSnapshot>;
    setSnapshot({
      devices: Array.isArray(data.devices) ? (data.devices as DeviceRecord[]) : [],
      outbox: Array.isArray(data.outbox)
        ? [...(data.outbox as OutboundRequest[])].sort((a, b) => b.createdAt - a.createdAt)
        : [],
    });
  }, []);

  const applyEnvelope = useCallback(
    (message: RealtimeEnvelope) => {
      if (message.type === 'realtime_snapshot') {
        applySnapshot(message.data);
        return;
      }
      if (message.type === 'device_presence') {
        const data = message.data as { device?: DeviceRecord } | undefined;
        if (!data?.device) return;
        setSnapshot((current) => ({
          ...current,
          devices: [
            ...current.devices.filter((device) => device.deviceId !== data.device?.deviceId),
            data.device as DeviceRecord,
          ].sort((a, b) => b.lastSeenAt - a.lastSeenAt),
        }));
        return;
      }
      if (message.type === 'sync_state') {
        const data = message.data as
          | {
              deviceId?: string;
              status?: DeviceRecord['lastSyncStatus'];
              lastSync?: number;
              messageCount?: number;
              newMessages?: number;
              error?: string;
            }
          | undefined;
        if (!data?.deviceId) return;
        if (data.status === 'success') setRefresh((value) => value + 1);
        setSnapshot((current) => ({
          ...current,
          devices: current.devices.map((device) =>
            device.deviceId === data.deviceId
              ? {
                  ...device,
                  lastSyncStatus: data.status,
                  lastSyncAt: data.lastSync,
                  lastSyncMessageCount: data.messageCount,
                  lastSyncNewMessages: data.newMessages,
                  lastSyncError: data.error,
                  status: 'online',
                  lastSeenAt: Date.now(),
                }
              : device
          ),
        }));
        return;
      }
      if (message.type === 'outbound_status' && message.data && typeof message.data === 'object') {
        const request = message.data as OutboundRequest;
        if (!request.requestId) return;
        setSnapshot((current) => ({
          ...current,
          outbox: [
            ...current.outbox.filter((item) => item.requestId !== request.requestId),
            request,
          ].sort((a, b) => b.createdAt - a.createdAt),
        }));
        return;
      }
    },
    [applySnapshot]
  );

  useEffect(() => {
    // Viewing as someone is read-only: never open their live device connection.
    if (!token || !userId || impersonatedUserId()) return;
    let cancelled = false;
    let retryTimer: number | null = null;
    let statusTimer: number | null = null;
    let heartbeatTimer: number | null = null;
    let retryAttempt = 0;
    let socket: WebSocket | null = null;
    setConnectionState('connecting');

    fetch('/api/realtime/status', { headers: { Authorization: `Bearer ${token}` } })
      .then((response) => {
        if (response.status === 401) clearSession();
        return response.ok ? response.json() : null;
      })
      .then((value) => {
        if (!cancelled && value) applySnapshot(value);
      })
      .catch(() => undefined);
    statusTimer = window.setInterval(() => {
      void fetch('/api/realtime/status', { headers: { Authorization: `Bearer ${token}` } })
        .then((response) => {
          if (response.status === 401) clearSession();
          return response.ok ? response.json() : null;
        })
        .then((value) => {
          if (!cancelled && value) applySnapshot(value);
        })
        .catch(() => undefined);
    }, 30_000);

    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const socketUrl = `${protocol}//${window.location.host}/api/realtime/socket?userId=${encodeURIComponent(userId)}&client=web`;

    const scheduleReconnect = () => {
      if (cancelled || retryTimer) return;
      const delay = Math.min(30_000, 1_000 * 2 ** Math.min(retryAttempt, 5));
      retryAttempt += 1;
      retryTimer = window.setTimeout(() => {
        retryTimer = null;
        connect();
      }, delay);
    };

    const connect = () => {
      if (cancelled || (socket && socket.readyState <= WebSocket.OPEN)) return;
      setConnectionState('connecting');
      try {
        socket = new WebSocket(socketUrl);
        socketRef.current = socket;
        socket.onopen = () => {
          retryAttempt = 0;
          socket?.send(JSON.stringify({ type: 'auth', data: { token }, timestamp: Date.now() }));
        };
        socket.onmessage = (event) => {
          try {
            const message = JSON.parse(String(event.data)) as RealtimeEnvelope;
            if (message.type === 'auth_success') {
              setConnectionState('live');
              setRefresh((value) => value + 1);
              if (heartbeatTimer) window.clearInterval(heartbeatTimer);
              heartbeatTimer = window.setInterval(() => {
                if (socket?.readyState === WebSocket.OPEN) {
                  socket.send(JSON.stringify({ type: 'ping', timestamp: Date.now() }));
                }
              }, 30_000);
            }
            if (
              message.type === 'error' &&
              (message.data as { code?: string } | undefined)?.code === 'invalid_token'
            ) {
              clearSession();
              socket?.close(1000, 'Session expired');
              return;
            }
            applyEnvelope(message);
          } catch {
            // Ignore malformed frames; the Worker validates and closes invalid peers.
          }
        };
        socket.onerror = () => {
          if (!cancelled) setConnectionState('offline');
        };
        socket.onclose = () => {
          if (socketRef.current === socket) socketRef.current = null;
          socket = null;
          if (!cancelled) {
            setConnectionState('offline');
            scheduleReconnect();
          }
        };
      } catch {
        socket = null;
        setConnectionState('offline');
        scheduleReconnect();
      }
    };

    connect();

    return () => {
      cancelled = true;
      if (retryTimer) window.clearTimeout(retryTimer);
      if (statusTimer) window.clearInterval(statusTimer);
      if (heartbeatTimer) window.clearInterval(heartbeatTimer);
      retryTimer = null;
      if (socketRef.current === socket) socketRef.current = null;
      socket?.close(1000, 'Page closed');
    };
  }, [applyEnvelope, applySnapshot, clearSession, token, userId]);

  const sendMessage = async (event: React.FormEvent) => {
    event.preventDefault();
    setError('');
    setSending(true);
    const requestId = crypto.randomUUID();
    const body = {
      requestId,
      platform,
      recipient: recipient.trim(),
      text: messageText.trim(),
    };
    try {
      let response: Response | null = null;
      let data: { request?: OutboundRequest; error?: string } = {};
      for (let attempt = 0; attempt < 2; attempt += 1) {
        try {
          response = await fetch('/api/realtime/send', {
            method: 'POST',
            headers: { ...authHeaders(token), 'Content-Type': 'application/json' },
            body: JSON.stringify(body),
          });
          data = (await response.json()) as { request?: OutboundRequest; error?: string };
          break;
        } catch (requestError) {
          if (attempt === 1) throw requestError;
        }
      }
      if (!response?.ok || !data.request?.requestId) {
        throw new Error(data.error || 'Unable to queue this message.');
      }
      if (sessionTokenRef.current !== token) return;
      const queuedRequest = data.request;
      setSnapshot((current) => ({
        ...current,
        outbox: [
          queuedRequest,
          ...current.outbox.filter((item) => item.requestId !== queuedRequest.requestId),
        ].sort((a, b) => b.createdAt - a.createdAt),
      }));
      setMessageText('');
    } catch (sendError) {
      if (sessionTokenRef.current !== token) return;
      setError(sendError instanceof Error ? sendError.message : 'Unable to queue this message.');
    } finally {
      setSending(false);
    }
  };

  // Signing in happens over the home page, and comes back here after.
  if (!token || !userId || isTokenExpired(token))
    return (
      <Navigate
        to={`/?sign-in=${encodeURIComponent(location.pathname + location.search)}`}
        replace
      />
    );

  const onlineDevices = snapshot.devices.filter((device) => device.status === 'online');
  const senderDevices = onlineDevices.filter((device) =>
    device.capabilities.sendPlatforms.includes(platform)
  );

  return (
    <main className="sync-page workspace-shell">
      {viewingAs && <ImpersonationBanner phoneNumber={viewingAs.phoneNumber} />}
      <aside className="workspace-sidebar">
        <div className="workspace-brand">
          <NavBarLogo />
        </div>
        <nav className="workspace-primary-nav" aria-label="Workspace">
          <AppLink
            to="/app"
            className={isAsk ? 'active' : ''}
            aria-current={isAsk ? 'page' : undefined}
          >
            <span aria-hidden="true">✦</span> Ask
          </AppLink>
          <AppLink
            to="/app/results"
            className={isResults ? 'active' : ''}
            aria-current={isResults ? 'page' : undefined}
          >
            <span aria-hidden="true">◈</span> Your results
          </AppLink>
          <AppLink
            to="/app/people"
            className={isPeople ? 'active' : ''}
            aria-current={isPeople ? 'page' : undefined}
          >
            <span aria-hidden="true">◎</span> Relationships
          </AppLink>
        </nav>
        <div className="workspace-sidebar-bottom">
          <AppLink
            to="/app/settings"
            className={isSettings || tab === 'conversations' ? 'active' : ''}
            aria-current={isSettings ? 'page' : undefined}
          >
            {ownPhoto.photoUrl ? (
              <img
                className="sidebar-own-photo"
                src={ownPhoto.photoUrl}
                alt=""
                onError={ownPhoto.onError}
              />
            ) : (
              <span aria-hidden="true">⚙</span>
            )}{' '}
            Settings & sync
          </AppLink>
          <span className={`sync-connection sync-connection-${connectionState}`}>
            <i />
            {viewingSomeone
              ? 'Read-only'
              : connectionState === 'live'
                ? 'Connected'
                : connectionState === 'connecting'
                  ? 'Connecting…'
                  : 'Offline'}
          </span>
        </div>
      </aside>
      <div
        className={`workspace-content ${isAsk || isResults || isPeople ? 'workspace-content-pane' : ''}`}
      >
        {isAsk && !workspace && (
          <div className="workspace-loading" role="status">
            <p>{workspaceError || 'Opening your space…'}</p>
            {workspaceError && (
              <button className="sync-text-button" onClick={() => setRefresh((value) => value + 1)}>
                Try again
              </button>
            )}
          </div>
        )}
        {workspace && (
          <div className="workspace-ask-pane" hidden={!isAsk}>
            <WorkspaceCoach
              token={token}
              hasMessages={workspace.profile.messageCount > 0}
              canOpenSources={isStaff}
            />
          </div>
        )}
        {isResults && (
          <WorkspaceResults key={token} token={token} selected={detail} canOpenSources={isStaff} />
        )}
        {isPeople && (
          <WorkspacePeople key={token} token={token} selected={detail} canOpenSources={isStaff} />
        )}
        {tab === 'conversations' && (
          <section key={tab} className="workspace-route">
            {workspaceError && (
              <div className="sync-error" role="alert">
                {workspaceError}
                <button
                  type="button"
                  className="sync-text-button"
                  onClick={() => setRefresh((value) => value + 1)}
                >
                  Try again
                </button>
              </div>
            )}
            {!workspace && !workspaceError && (
              <div className="workspace-loading" role="status">
                <span className="workspace-loading-bar" />
                <p>Gathering your threads…</p>
              </div>
            )}
            {adminSession && !isStaff && <Navigate to="/app" replace />}
            {workspace && isStaff && (
              <WorkspaceConversations
                data={workspace}
                token={token}
                refresh={refresh}
                loadMore={() => void loadMoreConversations()}
              />
            )}
            {isStaff && !viewingAs && senderDevices.length > 0 && (
              <details className="sync-panel sync-send-panel">
                <summary>Send an iMessage from the web</summary>
                <div className="sync-panel-heading">
                  <div>
                    <span className="sync-eyebrow">Through your connected Mac</span>
                  </div>
                </div>
                <p className="sync-muted">
                  Send an iMessage through your connected Mac. An offline request waits for your Mac
                  to reconnect.
                </p>
                <form onSubmit={sendMessage} className="sync-form">
                  <label htmlFor="send-platform">Platform</label>
                  <select
                    id="send-platform"
                    value={platform}
                    onChange={(event) => setPlatform(event.target.value)}
                  >
                    <option value="imessage">iMessage</option>
                  </select>
                  <label htmlFor="send-recipient">Recipient</label>
                  <input
                    id="send-recipient"
                    value={recipient}
                    onChange={(event) => setRecipient(event.target.value)}
                    placeholder="Phone number or handle"
                    required
                  />
                  <label htmlFor="send-text">Message</label>
                  <textarea
                    id="send-text"
                    value={messageText}
                    onChange={(event) => setMessageText(event.target.value)}
                    placeholder="Write a message…"
                    maxLength={10000}
                    required
                  />
                  <button className="btn btn-primary" type="submit" disabled={sending}>
                    {sending
                      ? 'Queueing…'
                      : senderDevices.length
                        ? 'Send from device'
                        : 'Queue for a sender'}
                  </button>
                </form>
                {snapshot.outbox.length > 0 && (
                  <div className="sync-outbox">
                    <strong>Recent requests</strong>
                    {snapshot.outbox.slice(0, 4).map((request) => (
                      <div className="sync-outbox-row" key={request.requestId}>
                        <span>
                          {request.platform} · {request.recipient}
                        </span>
                        <span
                          className={`sync-request-status sync-request-status-${request.status}`}
                        >
                          {request.status === 'sent' ? 'Handed to Messages' : request.status}
                        </span>
                        {request.error && <small>{request.error}</small>}
                      </div>
                    ))}
                  </div>
                )}
              </details>
            )}
          </section>
        )}
        {isSettings && (
          <div className="workspace-route">
            <header className="workspace-heading workspace-heading-compact">
              <div className="workspace-settings-title">
                <h1>Settings & sync</h1>
                <div className="workspace-settings-actions">
                  {!viewingAs && (
                    <button className="sync-text-button" type="button" onClick={clearSession}>
                      Sign out
                    </button>
                  )}
                </div>
              </div>
              <p>The more you connect, the better SoulThread gets to know you.</p>
            </header>
            {snapshot.devices.map((device) => {
              const notice = deviceSyncNotice(device);
              return notice ? (
                <p
                  className={`workspace-sync-notice${notice.failed ? ' workspace-sync-notice-failed' : ''}`}
                  role={notice.failed ? 'alert' : 'status'}
                  key={device.deviceId}
                >
                  {notice.text}
                </p>
              ) : null;
            })}
            <div className="workspace-connections-container">
              <div className="workspace-connections">
                {AUTO_SYNC_PROVIDERS.map((provider) => {
                  const devices = providerDevices(provider, snapshot.devices);
                  // Kept in sync on SoulThread's servers rather than by a Mac.
                  const onServers = cloud.connections.find(
                    (connection) =>
                      connection.provider === provider && connection.status === 'active'
                  );
                  const statuses = devices.map((device) =>
                    providerDeviceStatus(provider, device, connectionState === 'live')
                  );
                  const summary =
                    statuses.find((status) => status.tone === 'warning') ||
                    statuses.find((status) => status.tone === 'success') ||
                    statuses[0];
                  const deviceDetail = (device: DeviceRecord, index: number) =>
                    device.status === 'offline'
                      ? `last seen ${formatRelativeTime(device.lastSeenAt).toLowerCase()}`
                      : statuses[index].lastSync
                        ? `synced ${formatRelativeTime(statuses[index].lastSync).toLowerCase()}`
                        : 'history & live sync';
                  return (
                    <MessagingConnectionCard
                      provider={provider}
                      key={provider}
                      style={{ minWidth: 0 }}
                      status={
                        summary?.label ||
                        (onServers
                          ? 'Connected'
                          : viewingSomeone
                            ? 'Live status hidden'
                            : connectionState === 'live'
                              ? 'Not connected'
                              : 'Checking…')
                      }
                      tone={summary?.tone ?? (onServers ? 'success' : undefined)}
                      detail={
                        onServers && !devices.length
                          ? onServers.lastImportAt
                            ? `On the web · synced ${formatRelativeTime(onServers.lastImportAt).toLowerCase()}`
                            : 'On the web · bringing in your history'
                          : devices.length === 1
                            ? statuses[0].error ||
                              `${devices[0].deviceName} · ${deviceDetail(devices[0], 0)}`
                            : devices.length > 1
                              ? `${devices.length} Macs`
                              : undefined
                      }
                      action={
                        onServers && !viewingSomeone ? (
                          <button
                            className="btn btn-secondary btn-small"
                            type="button"
                            onClick={() => void cloud.disconnect(onServers.provider)}
                          >
                            Disconnect
                          </button>
                        ) : // Once a Mac is linked, apps are connected from that Mac, not downloaded again.
                        !snapshot.devices.length && connectionState === 'live' ? (
                          <Link to="/download" className="btn btn-secondary btn-small">
                            Get the Mac app ↗
                          </Link>
                        ) : null
                      }
                    >
                      {devices.length > 1 &&
                        devices.map((device, index) => (
                          <div className="workspace-connection-device" key={device.deviceId}>
                            <strong>{device.deviceName}</strong>
                            <span>
                              {statuses[index].label} · {deviceDetail(device, index)}
                            </span>
                            {statuses[index].error && (
                              <p className="sync-error" role="alert">
                                {statuses[index].error}
                              </p>
                            )}
                          </div>
                        ))}
                    </MessagingConnectionCard>
                  );
                })}
              </div>
            </div>
            {!viewingSomeone && (connecting || connectable.length > 0) && (
              <section className="workspace-cloud-connect">
                <h2>
                  No Mac? Keep {messengerNames(connecting ? [connecting] : connectable)} in sync
                  from here
                </h2>
                {connecting ? (
                  <MessengerConnect
                    provider={connecting}
                    token={token}
                    onConnected={() => void cloud.reload()}
                    onClose={() => setConnecting(null)}
                  />
                ) : (
                  <MessengerButtons
                    providers={connectable}
                    verb="Connect"
                    onChoose={setConnecting}
                  />
                )}
              </section>
            )}
            <p className="workspace-connections-note">
              {!snapshot.devices.length && activeOnServers.length > 0
                ? `${messengerNames(activeOnServers, 'and')} ${activeOnServers.length > 1 ? 'stay' : 'stays'} in sync from here. For iMessage and the rest, sign in to SoulThread on your Mac.`
                : connectionState === 'live' && !snapshot.devices.length
                  ? 'Open SoulThread on your Mac and sign in with the same number to start syncing.'
                  : 'Keep SoulThread open on your Mac to sync new messages. Manage connections in the Mac app.'}
            </p>
            <div className="workspace-settings-links">
              {isStaff && (
                <AppLink to="/app/conversations">Browse your synced conversations →</AppLink>
              )}
              <span>{workspace?.profile.messageCount.toLocaleString() || '0'} messages synced</span>
            </div>
          </div>
        )}
        {error && (
          <p className="sync-error" role="alert">
            {error}
          </p>
        )}
      </div>
    </main>
  );
};

export default RealtimePage;
